import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useAdminHelpCenterSearchIntents } from '@dashboard/hooks/queries/useAdminQueries';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import Link from '@dashboard/components/Link';

const HelpCenterSearchIntents = () => {
  const history = useHistory();
  const query = useUrlQuery();
  const { searchInput, setSearchInput, search } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(
    query.sortDirection ? Number(query.sortDirection) : -1,
  );
  const [limit] = useState(20);

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        limit,
      }),
    });
  }, [sortBy, sortDirection, search, limit]);

  const filters = {
    sortBy,
    sortDirection,
    search,
    limit,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useAdminHelpCenterSearchIntents(filters);

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'query',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Search query" />,
        cell: ({ row }) => row.getValue('query'),
        enableSorting: true,
      },
      {
        accessorKey: 'seller',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Merchant" />,
        cell: ({ row }) => {
          const seller = row.original.seller;
          if (!seller) return null;
          return (
            <Link href={`/admin/merchants/${seller._id}`}>{seller.displayName || seller.name}</Link>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
        cell: ({ row }) => format(new Date(row.getValue('createdAt')), 'd MMM, yyyy, HH:mm'),
        enableSorting: true,
      },
    ],
    [],
  );

  return (
    <div className="pr-4">
      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        toolbar={{
          showSearch: true,
          searchPlaceholder: 'Search by name, email, ID',
        }}
        globalFilter={searchInput}
        onGlobalFilterChange={setSearchInput}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No search intents found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default HelpCenterSearchIntents;
